import { useEffect, useReducer, useState } from 'react'
import { useLocation } from 'react-router-dom'
import SmartNavigator from '../generic/SmartNavigator'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { apiPost } from '../generic/Api_Functions'
import OrderSelector from '../materialreception/OrderSelector'
import ReportTheoreticalQuantity from '../materialreception/ReportTheoreticalQuantity'
import ReportQuantity from '../materialreception/ReportQuantity'
import QualityReportQuantity from '../materialreception/QualityReportQuantity'
import Papa from 'papaparse'
import useGetExtendedColumns from '../generic/hooks/useGetExtendedColumns/useGetExtendedColumns'

/**
 * @typedef {import('../generic/utilities/objectStructures/materialreception/materialReception').DeliveryNoteDataAction} DeliveryNoteDataAction DataAction object
 * @typedef {import('../generic/utilities/objectStructures/materialreception/materialReception').DeliveryNote} DeliveryNote Data object
 * @typedef {import('../generic/utilities/objectStructures/materialreception/materialReception').Order} Order Order object
 * @typedef {import('../generic/utilities/objectStructures/materialreception/materialReception').PurchaseOrderTheoreticalReception} PurchaseOrderTheoreticalReception PurchaseOrderTheoreticalReception object
 * @typedef {import('../generic/utilities/objectStructures/materialreception/materialReception').PurchaseOrderReception} PurchaseOrderReception PurchaseOrderReception object
 */

const MainMaterialReception = () => {
  const { t_ } = useSmartTranslation()
  const [path, setPath] = useState(0)
  /** @type {[import('../generic/utilities/objectStructures/materialreception/materialReception').Requirement[], React.Dispatch<SetStateAction<import('../generic/utilities/objectStructures/materialreception/materialReception').Requirement[]>>]} */
  const [requirements, setRequirements] = useState(null)
  const [lastVersion, setLastVersion] = useState()
  const location = useLocation()
  const extendedColumns = useGetExtendedColumns('raw_material_reference')
  useEffect(() => {
    const readCsvFile = async () => {
      const data = await fetch('/GDELS/requirements/versions.txt')
      const versions = await data.json()
      setLastVersion((versions[versions.length - 1]))
      const newRequirements = []
      for (const version of versions) {
        Papa.parse('/GDELS/requirements/requirements_' + version + '.csv', {
          download: true,
          delimiter: ';',
          header: false,
          skipEmptyLines: true,
          complete: (result) => {
            const headerInfo = result.data[0]
            const newRequirement = { documentName: headerInfo[0], revision: headerInfo[1], fileVersion: version, codes: [] }
            for (let i = 1; i < result.data.length; i++) {
              const row = result.data[i]
              const code = { code: row[0], values: new Map() }
              for (let j = 1; j < row.length; j++) {
                code.values.set(j, row[j])
              }
              newRequirement.codes.push(code)
            }
            newRequirements.push(newRequirement)
          },
          error: (err) => {
            console.error('Error leyendo CSV:', err)
          }
        })
      }
      setRequirements(newRequirements)
    }
    if (!requirements) {
      readCsvFile()
    }
  }, [requirements])

  /**
   * @param {number} recordCode
   * @param {string} deliveryNote
   * @param {Order} order
   * @returns {PurchaseOrderTheoreticalReception}
   */
  const createNewReceptionFromOrder = (recordCode, deliveryNote, recordPos, order) => {
    return {
      id: null,
      status: 'PENDING',
      recordCode,
      recordPos,
      purchaseOrder: { id: order.id, name: order.purchaseOrderCode },
      lineOrderPosition: order.lineOrderPosition,
      rawMaterialReference: order.rawMaterialReference.name,
      revision: order.revisionCode,
      batch: 1,
      description: order.rawMaterialDescription,
      receivedTheoreticalQuantity: order.retroOrder,
      receivedQuantity: order.retroOrder,
      approvedQuantity: 0,
      rejectedQuantity: 0,
      deliveryNote: { id: null, name: deliveryNote }
    }
  }

  /**
   * @param {DeliveryNote} oldData Old EditingData object
   * @param {DeliveryNoteDataAction} action EditingDataAction object
   */
  const currentAction = (oldData, action) => {
    switch (action.type) {
      case 'SET_DELIVERY_NOTE':
        if (oldData?.recordCode && (oldData?.recordCode !== action.deliveryNote.recordCode)) {
          if (action.desiredPath !== undefined) {
            setPath(action.desiredPath)
          } else {
            setPath(0)
          }
        }
        return action.deliveryNote
      case 'ADD_ORDER':
        return { ...oldData, purchaseOrderTheoreticalReceptions: [...oldData.purchaseOrderTheoreticalReceptions, createNewReceptionFromOrder(oldData.recordCode, oldData.number, oldData.purchaseOrderTheoreticalReceptions.length + 1, action.order)] }
      case 'RESET_VALUES':
        setPath(0)
        return null
      case 'UPDATE_RECEPTION': {
        const newData = { ...oldData }
        newData.purchaseOrderTheoreticalReceptions = newData.purchaseOrderTheoreticalReceptions.map(recep => {
          if (recep.recordCode === action.reception.recordCode && recep.recordPos === action.reception.recordPos) {
            return action.reception
          }
          return recep
        })

        return newData
      }
      default:
        throw new Error('Invalid action type')
    }
  }

  /** @type {[DeliveryNote, React.Dispatch<DeliveryNoteDataAction>]} */
  const [deliveryNote, dispatchDeliveryNote] = useReducer(currentAction, null)

  useEffect(() => {
    const navState = location?.state || null
    if (!navState) return

    if (navState.window) {
      const map = {
        ORDER_SELECTOR: 0,
        THEORETICAL_QUANTITY: 1,
        REAL_QUANTITY: 2,
        QUALITY_QUANTITY: 3
      }
      const idx = map[navState.window]
      if (idx !== undefined) {
        dispatchDeliveryNote({ type: 'SET_DELIVERY_NOTE', deliveryNote: navState.deliveryNote, desiredPath: idx })
        setPath(idx)
      } else {
        dispatchDeliveryNote({ type: 'SET_DELIVERY_NOTE', deliveryNote: navState.deliveryNote })
      }
    } else if (navState.deliveryNote) {
      dispatchDeliveryNote({ type: 'SET_DELIVERY_NOTE', deliveryNote: navState.deliveryNote })
    }
  }, [location])

  const onSave = async (recordCode, number) => {
    const deliveryNote = {
      recordCode,
      date: new Date(),
      number,
      purchaseOrderTheoreticalReceptions: null
    }
    const result = await apiPost('deliveryNote/create', deliveryNote)
    if (result) {
      dispatchDeliveryNote({ type: 'SET_DELIVERY_NOTE', deliveryNote: result })
    }
  }

  /**
   *
   * @returns {PurchaseOrderReception[]}
   */
  const getAllReceptions = () => {
    if (!deliveryNote?.purchaseOrderTheoreticalReceptions) {
      return []
    }
    const result = []
    deliveryNote.purchaseOrderTheoreticalReceptions.forEach((purchaseOrderTheoreticalReception) => {
      if (purchaseOrderTheoreticalReception.purchaseOrderReceptions) {
        result.push(...purchaseOrderTheoreticalReception.purchaseOrderReceptions)
      }
    })
    return result
  }

  const getAllQualityReceptions = () => {
    const data = []
    getAllReceptions().forEach(reception => {
      if (reception.receivedQuantity != null && ((!reception.serializable) || (reception.serializable && reception.batchNumber !== null))) {
        data.push(reception)
      }
    })
    return data
  }

  const getSelectedOrdersQuantity = () => {
    if (!deliveryNote || !deliveryNote.purchaseOrderTheoreticalReceptions || deliveryNote.purchaseOrderTheoreticalReceptions.length === 0) {
      return 0
    }
    return deliveryNote.purchaseOrderTheoreticalReceptions.length
  }

  const getReportedTheoreticalQuantity = () => {
    if (getSelectedOrdersQuantity() === 0) {
      return 0
    }
    return deliveryNote.purchaseOrderTheoreticalReceptions.filter((reception) => reception.receivedTheoreticalQuantity !== null).length
  }

  const getRealQuantity = () => {
    if (getReportedTheoreticalQuantity() === 0) {
      return 0
    }

    let count = 0
    getAllReceptions().forEach(reception => {
      if (reception.receivedQuantity != null && ((!reception.serializable) || (reception.serializable && reception.batchNumber !== null))) {
        count++
      }
    })

    return count
  }

  const getReportedQualitativeQuantity = () => {
    if (getRealQuantity() === 0) {
      return 0
    }

    return getAllReceptions().filter((reception) => reception.status === 'QUALITY').length
  }

  const getTheoreticalQuantityTitle = () => {
    if (getSelectedOrdersQuantity() === 0) {
      return t_('Theoretical quantity')
    }

    return t_('Theoretical quantity') + ' (' + getReportedTheoreticalQuantity() + ' / ' + getSelectedOrdersQuantity() + ')'
  }

  const getRealQuantityTitle = () => {
    if (getReportedTheoreticalQuantity() === 0) {
      return t_('Real quantity')
    }

    return t_('Real quantity') + ' (' + getRealQuantity() + ' / ' + getAllReceptions().length + ')'
  }

  const getReportedQualitativeQuantityTitle = () => {
    if (getRealQuantity() === 0) {
      return t_('Qualitative quantity')
    }

    return t_('Qualitative quantity') + ' (' + getReportedQualitativeQuantity() + ' / ' + getRealQuantity() + ')'
  }

  const componentsInfo = [
    {
      title: t_('Select Order'),
      component: <OrderSelector dispatchDeliveryNote={dispatchDeliveryNote} deliveryNote={deliveryNote} onCreate={() => onSave()} lastQrVersion={lastVersion} rawMaterialExtendedColumns={extendedColumns} />,
      permissionCode: 'SELECT_ORDER_TAB'
    },
    {
      title: getTheoreticalQuantityTitle(),
      component: <ReportTheoreticalQuantity dispatchDeliveryNote={dispatchDeliveryNote} deliveryNote={deliveryNote} lastQrVersion={lastVersion} rawMaterialExtendedColumns={extendedColumns} />,
      permissionCode: 'THEORETICAL_QUANTITY_TAB',
      disableTab: deliveryNote?.id && getSelectedOrdersQuantity() === 0
    },
    {
      title: getRealQuantityTitle(),
      component: <ReportQuantity dispatchDeliveryNote={dispatchDeliveryNote} deliveryNote={deliveryNote} allReceptions={getAllReceptions()} lastQrVersion={lastVersion} requirements={requirements} rawMaterialExtendedColumns={extendedColumns} />,
      permissionCode: 'REAL_QUANTITY_TAB',
      disableTab: deliveryNote?.id && getReportedTheoreticalQuantity() === 0
    },
    {
      title: getReportedQualitativeQuantityTitle(),
      component: <QualityReportQuantity dispatchDeliveryNote={dispatchDeliveryNote} deliveryNote={deliveryNote} allReceptions={getAllQualityReceptions()} lastQrVersion={lastVersion} requirements={requirements} rawMaterialExtendedColumns={extendedColumns} />,
      permissionCode: 'QUALITATIVE_QUANTITY_TAB',
      disableTab: deliveryNote?.id && getRealQuantity() === 0
    }
  ]
  return (
    <div>
      <SmartNavigator componentsInfo={componentsInfo} selected={path} setSelected={setPath} />
    </div>
  )
}
export default MainMaterialReception
